/**
 * Pure rect geometry in plane (u,v) space + plane↔world mapping. The capture rect
 * is the source of truth as { center, halfW, halfH, angle }; these functions
 * convert it to world corners, hit-test pointer rays against the plane, and apply
 * handle drags. No Babylon, no engine — individually unit-tested.
 */
import type { PlaneBasis, Vec2, Vec3 } from '../../solver/types';
import type { RectState } from '../../core/types';
import { add, dot, scale, sub } from '../../solver/vec';

export type RectSide = 'left' | 'right' | 'top' | 'bottom';
export type RectCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/** Plane (u,v) → world point. */
export const planeToWorld = (basis: PlaneBasis, u: number, v: number): Vec3 =>
  add(basis.origin, add(scale(basis.right, u), scale(basis.up, v)));

/** World point → plane (u,v) (assumes the point lies on / near the plane). */
export const worldToPlane = (basis: PlaneBasis, world: Vec3): Vec2 => {
  const d = sub(world, basis.origin);
  return [dot(d, basis.right), dot(d, basis.up)];
};

/** Intersect a ray with the plane; null if (near) parallel. */
export function planePointFromRay(
  rayOrigin: Vec3,
  rayDir: Vec3,
  planeOrigin: Vec3,
  planeNormal: Vec3,
): Vec3 | null {
  const denom = dot(rayDir, planeNormal);
  if (Math.abs(denom) < 1e-6) return null;
  const t = dot(sub(planeOrigin, rayOrigin), planeNormal) / denom;
  if (t < 0) return null; // behind the ray origin
  return add(rayOrigin, scale(rayDir, t));
}

/** The rect's 4 corners in plane (u,v) space, order: BL, BR, TR, TL. */
export function rectCornersPlane(rect: RectState): Vec2[] {
  const c = Math.cos(rect.angle);
  const s = Math.sin(rect.angle);
  const local: Vec2[] = [
    [-rect.halfW, -rect.halfH],
    [rect.halfW, -rect.halfH],
    [rect.halfW, rect.halfH],
    [-rect.halfW, rect.halfH],
  ];
  return local.map(([x, y]) => [rect.center[0] + x * c - y * s, rect.center[1] + x * s + y * c]);
}

/** The rect's 4 corners in world space. */
export function rectCornersWorld(basis: PlaneBasis, rect: RectState): Vec3[] {
  return rectCornersPlane(rect).map(([u, v]) => planeToWorld(basis, u, v));
}

/** Convert a plane point into the rect's local (un-rotated, centred) frame. */
function toLocal(rect: RectState, p: Vec2): Vec2 {
  const dx = p[0] - rect.center[0];
  const dy = p[1] - rect.center[1];
  const c = Math.cos(-rect.angle);
  const s = Math.sin(-rect.angle);
  return [dx * c - dy * s, dx * s + dy * c];
}

/**
 * Drag one side to a plane point, anchoring the OPPOSITE side. The opposite edge
 * stays fixed; the dragged edge follows the pointer; width/height ≥ 2·minHalf.
 */
export function dragSide(rect: RectState, side: RectSide, planePoint: Vec2, minHalf: number): RectState {
  const local = toLocal(rect, planePoint);
  const axis: 'x' | 'y' = side === 'left' || side === 'right' ? 'x' : 'y';
  const dir = side === 'right' || side === 'top' ? 1 : -1;
  const half = axis === 'x' ? rect.halfW : rect.halfH;
  const e = axis === 'x' ? local[0] : local[1]; // dragged edge coordinate
  const fixedEdge = -dir * half;
  const newHalf = Math.max(minHalf, Math.abs(e - fixedEdge) / 2);
  const centerAlong = fixedEdge + dir * newHalf; // local center keeping the fixed edge put

  // Shift the center along the (rotated) axis by centerAlong.
  const c = Math.cos(rect.angle);
  const s = Math.sin(rect.angle);
  const axisVec: Vec2 = axis === 'x' ? [c, s] : [-s, c];
  const center: Vec2 = [
    rect.center[0] + axisVec[0] * centerAlong,
    rect.center[1] + axisVec[1] * centerAlong,
  ];

  return {
    center,
    halfW: axis === 'x' ? newHalf : rect.halfW,
    halfH: axis === 'y' ? newHalf : rect.halfH,
    angle: rect.angle,
  };
}

/**
 * Drag one corner to a plane point, anchoring the OPPOSITE corner. Both width and
 * height follow the pointer (two sides adjust at once); each axis is independently
 * floored at minHalf, mirroring dragSide.
 */
export function dragCorner(rect: RectState, corner: RectCorner, planePoint: Vec2, minHalf: number): RectState {
  const local = toLocal(rect, planePoint);
  const dirX = corner === 'top-right' || corner === 'bottom-right' ? 1 : -1; // +x = right
  const dirY = corner === 'top-left' || corner === 'top-right' ? 1 : -1; // +y = top
  const fixedX = -dirX * rect.halfW; // the anchored (opposite) edges, in local space
  const fixedY = -dirY * rect.halfH;
  const newHalfW = Math.max(minHalf, Math.abs(local[0] - fixedX) / 2);
  const newHalfH = Math.max(minHalf, Math.abs(local[1] - fixedY) / 2);
  const centerAlongX = fixedX + dirX * newHalfW; // local center keeping the fixed corner put
  const centerAlongY = fixedY + dirY * newHalfH;

  // Rotate the local center offset back into plane space.
  const c = Math.cos(rect.angle);
  const s = Math.sin(rect.angle);
  const center: Vec2 = [
    rect.center[0] + c * centerAlongX - s * centerAlongY,
    rect.center[1] + s * centerAlongX + c * centerAlongY,
  ];
  return { center, halfW: newHalfW, halfH: newHalfH, angle: rect.angle };
}

/**
 * Rotate the rect around its center toward a plane point. `grabOffset` is the
 * angle (point→center) captured at gesture start, so rotation tracks the grab
 * smoothly. Free rotation — no cardinal snapping, so fine adjustments near 0°
 * (and any angle) stay accurate.
 */
export function dragRotate(rect: RectState, planePoint: Vec2, grabOffset: number): RectState {
  const raw = Math.atan2(planePoint[1] - rect.center[1], planePoint[0] - rect.center[0]);
  const angle = raw - grabOffset;
  return { ...rect, angle };
}
